import * as action from '@/store/action.types';
import * as mutation from '@/store/mutation.types';
import ApiService from '@/common/api.service';
import { 
  FETCH_DASHBOARD_URL,
  FETCH_PLAYER_TEAMS_URL,
  FETCH_TIMELINE_FOR_TEAM_URL,
  FETCH_DASHBOARD_FOR_TEAM_URL,
  FETCH_TIMELINE_FOR_INDIVIDUAL_URL,
  FETCH_DASHBOARD_FOR_INDIVIDUAL_ACTIVITIES_URL,
  FETCH_DASHBOARD_FOR_ALL_TEAMS_URL,
  FETCH_TIMELINE_FOR_ALL_TEAMS_URL
} from '@/constants/ApiConstants';
import STATUS_CODES from '@/common/statusCodes';
import { METRICS } from '@/constants/DashboardConstants';
import { SESSION_METRICS, SESSION_METRICS_STATE_KEYS } from '@/constants/SessionConsts';
import { ENTITIES, TIME_RANGE } from '@/constants/DashboardConstants';

const state = {
  teams: [],
  selectedTeamId: null,
  selectedTeam: null,
  selectedEntity: ENTITIES.ALL_TEAMS,
  selectedTimeRange: TIME_RANGE.SESSION,
  timeRangeFrom: null,
  timeRangeTo: null,
  timeAbove80PercentHrSeconds: null,
  ap: null,
  distance: null,
  workload: null,
  currentMetric: METRICS.TIME_ABOVE_80_PERCENT_HR,
  teamData: {},
  individualTimeline: {
    [METRICS.TIME_ABOVE_80_PERCENT_HR]: null,
    [METRICS.AP_PERCENTAGE]: null,
    [METRICS.DISTANCE]: null,
    [METRICS.WORKLOAD]: null,
  },
  allTeamsTimeLine: {
    [METRICS.TIME_ABOVE_80_PERCENT_HR]: null,
    [METRICS.AP_PERCENTAGE]: null,
    [METRICS.DISTANCE]: null,
    [METRICS.WORKLOAD]: null,
  },
  individualDashboard: {},
  allTeamsDashboard: {},
  timelineOnScreen: null,
  dashboardOnScreen: {},
  noData: false,
  noSessions: false,
  initialDataFetched: false,
  timelinePage:0,
  timelineItemsPerPage:screen.width <= 595? 7
  :screen.width <= 600? 7
  :screen.width <= 768? 14
  :screen.width <= 889? 14
  :30,
  timelineTotalPages : 1,
  timelineMinValue:0,
  timelineMaxValue:0,
  lastSessionData:{}
}

const getters = {
  teams: state => state.teams,
  selectedTeam: state => state.selectedTeam,
  timeAbove80PercentHrSeconds: state => state.timeAbove80PercentHrSeconds,
  ap: state => state.ap,
  distance: state => state.distance,
  workload: state => state.workload,
  teamData: state => state.teamData,
  individualTimeline: state => state.individualTimeline,
  currentMetric: state => state.currentMetric,
  selectedEntity: state => state.selectedEntity,
  selectedTimeRange: state => state.selectedTimeRange,
  timeRangeFrom: state => state.timeRangeFrom,
  timeRangeTo: state => state.timeRangeTo,
  timelineOnScreen: state => state.timelineOnScreen,
  dashboardOnScreen: state => state.dashboardOnScreen,
  noData: state => state.noData,
  noSessions: state => state.noSessions,
  initialDataFetched: state => state.initialDataFetched,
  timelinePage: state=>state.timelinePage,
  timelineItemsPerPage: state=>state.timelineItemsPerPage,
  timelineTotalPages: state=>state.timelineTotalPages,
  timelineMinValue: state=>state.timelineMinValue,
  timelineMaxValue: state=>state.timelineMaxValue,
  lastSessionData: state=>state.lastSessionData
}

const generateDateParams = (from,to)=>{
   let fromDate = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0)).toISOString()
   let toDate = new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59)).toISOString();
   const fromTPosition = fromDate.indexOf('T');
   const toTPosition = toDate.indexOf('T');
   const fromRequestString = fromDate.slice(0,fromTPosition);
   const toRequestString = toDate.slice(0,toTPosition);
   return{
      timeRangeFrom: fromRequestString,
      timeRangeTo: toRequestString
   }
}

const returnTimelineObject = (timeline,currentMetric)=>{
   if(isTimelineCached(timeline)){
         return timeline[SESSION_METRICS_STATE_KEYS[currentMetric]]
   }
   else return timeline;
}

const isTimelineCached = (timeline)=>{
   if(!timeline) return false;
   else{
      return (!!timeline[SESSION_METRICS_STATE_KEYS.TIME_ABOVE_80_PERCENT_HR] || 
      !!timeline[SESSION_METRICS_STATE_KEYS.AP] || 
      !!timeline[SESSION_METRICS_STATE_KEYS.DISTANCE] ||
      !!timeline[SESSION_METRICS_STATE_KEYS.WORKLOAD]);
   }
}

const generateDashboardParams = (timeRangeValue,metric,from=null,to=null) => {
   let returnMetric = metric === SESSION_METRICS.AP ? METRICS.AP_PERCENTAGE : metric;
   switch (timeRangeValue) {
      case TIME_RANGE.SESSION.value:
         return {
            timeRange:TIME_RANGE.SESSION.value,
            metric:returnMetric
         };
      case TIME_RANGE.WEEK.value:
         let toDate = new Date();
         let fromDate = new Date();
         fromDate.setDate(fromDate.getDate() - 7);
         return {
            metric:returnMetric,
            timeRange:TIME_RANGE.CUSTOM.value,
            ...generateDateParams(fromDate,toDate)
         };
      case TIME_RANGE.MONTH.value:
         let toDateMonth = new Date();
         let fromDateMonth = new Date();
         fromDateMonth.setDate(fromDateMonth.getDate() - 30);
         return {
            metric:returnMetric,
            timeRange:TIME_RANGE.CUSTOM.value,
            ...generateDateParams(fromDateMonth,toDateMonth)
         };
      case TIME_RANGE.CUSTOM.value:
         return {
            metric:returnMetric,
            timeRange:TIME_RANGE.CUSTOM.value,
            ...generateDateParams(from,to)
         };
      default:
         return null;
   }
}

const actions = {
  [action.FETCH_PLAYER_TEAMS] ({ commit, state, rootState }) {
    const playerId = rootState.player.account.playerId;

    return new Promise((resolve, reject) => {
      ApiService.get(FETCH_PLAYER_TEAMS_URL.replace(':playerId', playerId))
        .then(({ data }) => { 
          commit(mutation.UPDATE_TEAMS, data);

          if(state.selectedTeamId) {
            commit(mutation.UPDATE_SELECTED_TEAM, state.selectedTeamId);
          }
          else if (state.selectedEntity===ENTITIES.TEAM && state.selectedTeamId===null && data.length>0){
            commit(mutation.UPDATE_SELECTED_TEAM, data[0].teamId);
          }

          return resolve(data);
        })
        .catch(error => reject(error))
    })
  },
  [action.FETCH_DASHBOARD_DATA] ({ commit, state, rootState }) {
    const playerId = rootState.player.account.playerId;

    return new Promise((resolve, reject) => {
      ApiService.get(FETCH_DASHBOARD_URL.replace(':playerId', playerId), { 
        timeRange: state.selectedTimeRange.value
      })
        .then(({ data }) => {
           if(state.selectedTimeRange.value===TIME_RANGE.SESSION.value){
             commit(mutation.UPDATE_LAST_SESSION_DATA,data);
           }
           else{
            commit(mutation.UPDATE_TIMELINE, data.timeline);
            commit(mutation.UPDATE_TIME_ABOVE_80_PERCENT_HR, data.timeAbove80PercentHrSeconds);
            commit(mutation.UPDATE_AP, data.ap);
            commit(mutation.UPDATE_DISTANCE, data.distance);
            commit(mutation.UPDATE_WORKLOAD, data.workload);
            commit(mutation.UPDATE_SELECTED_TEAM, data.teamId);
  
            if(data.teamId) {
              commit(mutation.UPDATE_TEAM_DATA, { 
                data: data, 
                teamId: data.teamId
              });
            }
  
            commit(mutation.UPDATE_SELECTED_DASHBOARD, {
              timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
              ap: data.ap,
              distance: data.distance,
              workload: data.workload
            });
         }
         commit(mutation.UPDATE_INITIAL_DATA_FETCHED, true);
         commit(mutation.UPDATE_NO_SESSIONS, false);
          

          return resolve(data);
        })
        .catch(error => {
          commit(mutation.UPDATE_INITIAL_DATA_FETCHED, true);

          if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
            commit(mutation.UPDATE_NO_SESSIONS, true);
            commit(mutation.UPDATE_DASHBOARD_NO_DATA);
          }

          return reject(error);
        });
    })
  },
  [action.CHANGE_SELECTED_ENTITY] ({ commit, state, rootState }, entity) {
    commit(mutation.UPDATE_SELECTED_ENTITY, entity);
    const playerId = rootState.player.account.playerId;
    const { 
      timeRangeFrom, 
      timeRangeTo, 
      currentMetric, 
      selectedTimeRange 
    } = state;

    if(entity === ENTITIES.INDIVIDUAL) {
      return new Promise((resolve, reject) => {
        const dashboardParams = generateDashboardParams(selectedTimeRange.value,currentMetric,timeRangeFrom,timeRangeTo);
        ApiService.get(FETCH_DASHBOARD_FOR_INDIVIDUAL_ACTIVITIES_URL.replace(':playerId', playerId),dashboardParams )
          .then(({ data }) => {
            commit(mutation.UPDATE_SELECTED_DASHBOARD, {
              timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
              ap: data.ap,
              distance: data.distance,
              workload: data.workload
            });

            commit(mutation.UPDATE_INDIVIDUAL_DASHBOARD, {
              timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
              ap: data.ap,
              distance: data.distance,
              workload: data.workload
            });
            commit(mutation.UPDATE_INDIVIDUAL_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
            if(selectedTimeRange.value===TIME_RANGE.SESSION.value){
               commit(mutation.UPDATE_LAST_SESSION_DATA,data);
            }
            else{
               commit(mutation.UPDATE_TIMELINE, data.timeline);
            }
  
            return resolve(data);
          })
          .catch(error => {
            if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
              commit(mutation.UPDATE_DASHBOARD_NO_DATA);
            }

            return reject(error);
          });
      })
    } else if (entity === ENTITIES.ALL_TEAMS) {
      return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
        ApiService.get(FETCH_DASHBOARD_FOR_ALL_TEAMS_URL.replace(':playerId', playerId), dashboardParams)
          .then(({ data }) => {
            commit(mutation.UPDATE_SELECTED_DASHBOARD, {
              timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
              ap: data.ap,
              distance: data.distance,
              workload: data.workload
            });

            commit(mutation.UPDATE_ALL_TEAMS_DASHBOARD, {
              timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
              ap: data.ap,
              distance: data.distance,
              workload: data.workload
            });

            commit(mutation.UPDATE_ALL_TEAMS_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
            if(selectedTimeRange.value===TIME_RANGE.SESSION.value){
               commit(mutation.UPDATE_LAST_SESSION_DATA,data);
            }
            else{
               commit(mutation.UPDATE_TIMELINE, data.timeline);
            }
  
            return resolve(data);
          })
          .catch(error => {
            if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
              commit(mutation.UPDATE_DASHBOARD_NO_DATA);
            }

            return reject(error);
          });
      })
    }
  },
  [action.CHANGE_SELECTED_TEAM] ({ commit, state, rootState }, teamId) {
    const playerId = rootState.player.account.playerId;
    const { teamData, timeRangeFrom, timeRangeTo } = state;

    return new Promise((resolve, reject) => {
      commit(mutation.UPDATE_SELECTED_TEAM, teamId);

      if(!teamData[teamId] || !teamData[teamId].timelineValues[state.currentMetric] || !teamData[teamId].timelineValues[state.currentMetric].length) {
         const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
        ApiService.get(FETCH_DASHBOARD_FOR_TEAM_URL.replace(':playerId', playerId).replace(':teamId', teamId), dashboardParams)
          .then(({ data }) => {
            commit(mutation.UPDATE_SELECTED_DASHBOARD, {
              timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
              ap: data.ap,
              distance: data.distance,
              workload: data.workload
            });

            commit(mutation.UPDATE_TEAM_DATA, { 
              data: data, 
              teamId 
            });
            if(state.selectedTimeRange.value===TIME_RANGE.SESSION.value){
               commit(mutation.UPDATE_LAST_SESSION_DATA,data);
            }
            else{
               commit(mutation.UPDATE_TIMELINE, data.timeline);
            }
  
            return resolve(data);
          })
          .catch(error => {
            if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
              commit(mutation.UPDATE_DASHBOARD_NO_DATA);
            }

            return reject(error);
          });
      } else {
        //todo update dashboard
        commit(mutation.UPDATE_TIMELINE, teamData[teamId].timelineValues[state.currentMetric]);
        commit(mutation.UPDATE_SELECTED_DASHBOARD, {
          timeAbove80PercentHrSeconds: teamData[teamId].timeAbove80PercentHrSeconds,
          ap: teamData[teamId].ap,
          distance: teamData[teamId].distance,
          workload: teamData[teamId].workload
        });
      }
    })
  },
  [action.CHANGE_TIMERANGE] ({ commit, state, rootState }, timeRange) {
    const playerId = rootState.player.account.playerId;
    const { 
      selectedTeamId,
      timeRangeFrom,
      timeRangeTo,
      selectedEntity
    } = state;

    if(timeRange === TIME_RANGE.CUSTOM.value || timeRange===TIME_RANGE.SESSION.value) {
      commit(mutation.UPDATE_TIMERANGE, TIME_RANGE[timeRange])

    } else {
      commit(mutation.UPDATE_TIMERANGE, TIME_RANGE[timeRange]);

      if(selectedEntity === ENTITIES.TEAM) {
        return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(timeRange,state.currentMetric,timeRangeFrom,timeRangeTo);
          ApiService.get(FETCH_DASHBOARD_FOR_TEAM_URL.replace(':playerId', playerId).replace(':teamId', selectedTeamId), dashboardParams)
            .then(({ data }) => {
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }

              return reject(error);
            });
        });
      } else if (selectedEntity === ENTITIES.INDIVIDUAL) {
        return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
          ApiService.get(FETCH_DASHBOARD_FOR_INDIVIDUAL_ACTIVITIES_URL.replace(':playerId', playerId), dashboardParams)
            .then(({ data }) => {
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_INDIVIDUAL_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
              commit(mutation.UPDATE_INDIVIDUAL_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        })
      } else if (selectedEntity === ENTITIES.ALL_TEAMS) {
        return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
          ApiService.get(FETCH_DASHBOARD_FOR_ALL_TEAMS_URL.replace(':playerId', playerId), dashboardParams)
            .then(({ data }) => {
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_ALL_TEAMS_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_ALL_TEAMS_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        })
      }
    }
  },
  [action.CHANGE_SELECTED_METRIC] ({ commit, state, rootState }, metric) {
    const playerId = rootState.player.account.playerId;

    const { 
      teamData, 
      selectedEntity, 
      selectedTeamId, 
      individualTimeline,
      allTeamsTimeLine,
      timeRangeFrom,
      timeRangeTo,
    } = state;

    commit(mutation.UPDATE_SELECTED_METRIC, metric);

    if(metric === SESSION_METRICS.AP) {
      metric = METRICS.AP_PERCENTAGE;
    }

    if(selectedEntity === ENTITIES.TEAM) {
      if(teamData[selectedTeamId] && teamData[selectedTeamId].timelineValues[metric]) {
        commit(mutation.UPDATE_TIMELINE, teamData[selectedTeamId].timelineValues);

      } else {
        // get timelines for single team
        const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,metric,timeRangeFrom,timeRangeTo);
        return new Promise((resolve, reject) => (
          ApiService.get(FETCH_TIMELINE_FOR_TEAM_URL.replace(':playerId', playerId).replace(':teamId', selectedTeamId), dashboardParams)
            .then(({ data}) => {
              commit(mutation.UPDATE_TEAM_DATA, { 
                teamId: selectedTeamId, 
                timelineValues: data
              });

              commit(mutation.UPDATE_TIMELINE, data);

              resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
              
              return reject(error);
            })
        ))
      }
    } else if(selectedEntity === ENTITIES.ALL_TEAMS) {
      if(allTeamsTimeLine[metric]) {
        commit(mutation.UPDATE_TIMELINE, allTeamsTimeLine);

      } else {
        // get timelines for single team
        return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,metric,timeRangeFrom,timeRangeTo);
          return ApiService.get(FETCH_TIMELINE_FOR_ALL_TEAMS_URL.replace(':playerId', playerId), dashboardParams)
            .then(({ data}) => {
              commit(mutation.UPDATE_ALL_TEAMS_TIMELINE, { timeline: data, metric});
              commit(mutation.UPDATE_TIMELINE, data);

              resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
              return reject(error);
            })
        })
      }
      
    } else if(selectedEntity === ENTITIES.INDIVIDUAL) {
      if(individualTimeline[metric]) {
        commit(mutation.UPDATE_TIMELINE, individualTimeline);

      } else {

        // get timelines for single team
        
        // TO DO: Check and compare parameters with BE side
        // const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,metric,timeRangeFrom,timeRangeTo);
        // return new Promise((resolve, reject) => {
          // ApiService.get(FETCH_TIMELINE_FOR_INDIVIDUAL_URL.replace(':playerId', playerId), { 
          //   playerId, 
          //   teamId: selectedTeamId, 
          //   ...dashboardParams
          // })
          //   .then(({ data}) => {
          //     commit(mutation.UPDATE_INDIVIDUAL_TIMELINE, { timeline: data, metric });
          //     commit(mutation.UPDATE_TIMELINE, data);

          //     resolve(data);
          //   })
          //   .catch(error => {
          //     if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
          //       commit(mutation.UPDATE_DASHBOARD_NO_DATA);
          //     }
          //     return reject(error);
          //   })
        // })
      }
    }
  },
  [action.CHANGE_TIMERANGE_TO_DATE] ({ commit, state, rootState }, date) {
    commit(mutation.UPDATE_TIMERANGE_TO_DATE, date);

    if(state.timeRangeFrom) {
      const timeRange = state.selectedTimeRange.value;
      const { 
        selectedTeamId,
        timeRangeFrom,
        timeRangeTo,
        selectedEntity
      } = state;

      const playerId = rootState.player.account.playerId;

      if(selectedEntity === ENTITIES.TEAM) {
         return new Promise((resolve, reject) => {
            const dashboardParams = generateDashboardParams(timeRange,state.currentMetric,timeRangeFrom,timeRangeTo);
            ApiService.get(FETCH_DASHBOARD_FOR_TEAM_URL.replace(':playerId', playerId).replace(':teamId', selectedTeamId), dashboardParams)
            .then(({ data }) => {
               commit(mutation.RESET_TIMELINE_STATE);
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
              commit(mutation.UPDATE_TIMERANGE, TIME_RANGE[timeRange])
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        });
      } else if (selectedEntity === ENTITIES.ALL_TEAMS) {
         return new Promise((resolve, reject) => {
            const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
            ApiService.get(FETCH_DASHBOARD_FOR_ALL_TEAMS_URL.replace(':playerId', playerId), dashboardParams)
            .then(({ data }) => {
               commit(mutation.RESET_TIMELINE_STATE);
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_ALL_TEAMS_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_ALL_TEAMS_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        })
      } else if (selectedEntity === ENTITIES.INDIVIDUAL) {
         return new Promise((resolve, reject) => {
            const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
            ApiService.get(FETCH_DASHBOARD_FOR_INDIVIDUAL_ACTIVITIES_URL.replace(':playerId', playerId), dashboardParams)
            .then(({ data }) => {
               commit(mutation.RESET_TIMELINE_STATE);
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_INDIVIDUAL_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
              commit(mutation.UPDATE_INDIVIDUAL_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        })
      }
    }
  },
  [action.CHANGE_TIMERANGE_FROM_DATE] ({ commit, state, rootState }, date) {
    commit(mutation.UPDATE_TIMERANGE_FROM_DATE, date);

    if(state.timeRangeTo) {
      const playerId = rootState.player.account.playerId;
      const timeRange = state.selectedTimeRange.value;
      const { 
        selectedTeamId,
        timeRangeFrom,
        timeRangeTo,
        selectedEntity
      } = state;

      if(selectedEntity === ENTITIES.TEAM) {
         
        return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(timeRange,state.currentMetric,timeRangeFrom,timeRangeTo);
          ApiService.get(FETCH_DASHBOARD_FOR_TEAM_URL.replace(':playerId', playerId).replace(':teamId', selectedTeamId), dashboardParams)
            .then(({ data }) => {
               commit(mutation.RESET_TIMELINE_STATE);
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
              commit(mutation.UPDATE_TIMERANGE, TIME_RANGE[timeRange])
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        });
      } else if (selectedEntity === ENTITIES.ALL_TEAMS) {
        return new Promise((resolve, reject) => {
         const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
          ApiService.get(FETCH_DASHBOARD_FOR_ALL_TEAMS_URL.replace(':playerId', playerId), dashboardParams)
            .then(({ data }) => {
               commit(mutation.RESET_TIMELINE_STATE);
              commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_ALL_TEAMS_DASHBOARD, {
                timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                ap: data.ap,
                distance: data.distance,
                workload: data.workload
              });
  
              commit(mutation.UPDATE_ALL_TEAMS_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
              commit(mutation.UPDATE_TIMELINE, data.timeline);
    
              return resolve(data);
            })
            .catch(error => {
              if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                commit(mutation.UPDATE_DASHBOARD_NO_DATA);
              }
  
              return reject(error);
            });
        })
      } else if (selectedEntity === ENTITIES.INDIVIDUAL) {
          return new Promise((resolve, reject) => {
            const dashboardParams = generateDashboardParams(state.selectedTimeRange.value,state.currentMetric,timeRangeFrom,timeRangeTo);
            ApiService.get(FETCH_DASHBOARD_FOR_INDIVIDUAL_ACTIVITIES_URL.replace(':playerId', playerId), dashboardParams)
              .then(({ data }) => {
               commit(mutation.RESET_TIMELINE_STATE);
                commit(mutation.UPDATE_SELECTED_DASHBOARD, {
                  timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                  ap: data.ap,
                  distance: data.distance,
                  workload: data.workload
                });
    
                commit(mutation.UPDATE_INDIVIDUAL_DASHBOARD, {
                  timeAbove80PercentHrSeconds: data.timeAbove80PercentHrSeconds,
                  ap: data.ap,
                  distance: data.distance,
                  workload: data.workload
                });
                commit(mutation.UPDATE_INDIVIDUAL_TIMELINE, { timeline: data.timeline, metric: state.currentMetric});
                commit(mutation.UPDATE_TIMELINE, data.timeline);
      
                return resolve(data);
              })
              .catch(error => {
                if(error && error.status && error.status.code === STATUS_CODES.BP_404) {
                  commit(mutation.UPDATE_DASHBOARD_NO_DATA);
                }
    
                return reject(error);
              });
          })
      }
    }
  },
  [action.RESET_DASHBOARD_STATE] ({ commit }) {
    commit(mutation.RESET_DASHBOARD_STATE);
  },
  [action.UPDATE_INITIAL_DATA_FETCHED] ({ commit }, isFetched) {
    commit(mutation.UPDATE_INITIAL_DATA_FETCHED, isFetched);
  }
}

const mutations = {
  [mutation.UPDATE_TEAMS] (state, teams) {
    state.teams = teams;
  },
  [mutation.UPDATE_TIME_ABOVE_80_PERCENT_HR] (state, timeAbove80PercentHrSeconds) {
    state.timeAbove80PercentHrSeconds = Math.round(timeAbove80PercentHrSeconds / 60);
  },
  [mutation.UPDATE_AP] (state, ap) {
    state.ap = ap;
  },
  [mutation.UPDATE_DISTANCE] (state, distance) {
    state.distance = distance;
  },
  [mutation.UPDATE_WORKLOAD] (state, workload) {
    state.workload = workload;
  },
  [mutation.UPDATE_LAST_SESSION_DATA] (state, data) {
    state.lastSessionData = data;
  },
  [mutation.UPDATE_SELECTED_TEAM] (state, teamId) {
    if(teamId) {
      state.selectedTeamId = teamId;
      state.selectedEntity = ENTITIES.TEAM;

      if(state.teams.length) {
        state.selectedTeam = state.teams.filter(t => t.teamId === teamId)[0];
      }
    } else {
      state.selectedEntity = ENTITIES.INDIVIDUAL;
    }
  },
  [mutation.UPDATE_SELECTED_ENTITY] (state, entity) {
    state.selectedEntity = entity;
  },
  [mutation.UPDATE_TIMERANGE] (state, timeRange) {
    state.selectedTimeRange = timeRange;
  },
  [mutation.UPDATE_SELECTED_METRIC] (state, metric) {
    state.currentMetric = metric;
  },
  [mutation.UPDATE_TEAM_DATA] (state, { data, teamId, timelineValues }) {
    const { currentMetric } = state;

    if(!state.teamData[teamId]) {
      state.teamData[teamId] = {};
      state.teamData[teamId].timelineValues = {};
    }
    
    state.noData = false;

    if(data) {
      for(let property in data) {
        if(property !== 'timelineValues') {
          state.teamData[teamId][property] = data[property];
        }
      }

      switch (currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          state.teamData[teamId].timelineValues[METRICS.TIME_ABOVE_80_PERCENT_HR] = data.timelineValues;
          break;
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          state.teamData[teamId].timelineValues[METRICS.AP_PERCENTAGE] = data.timelineValues;
          break;
        case METRICS.DISTANCE:
          state.teamData[teamId].timelineValues[METRICS.DISTANCE] = data.timelineValues;
          break;
        case METRICS.WORKLOAD:
          state.teamData[teamId].timelineValues[METRICS.WORKLOAD] = data.timelineValues;
          break;
      }
    } else {
      switch (currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          state.teamData[teamId].timelineValues[METRICS.TIME_ABOVE_80_PERCENT_HR] = timelineValues;
          break;
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          state.teamData[teamId].timelineValues[METRICS.AP_PERCENTAGE] = timelineValues;
          break;
        case METRICS.DISTANCE:
          state.teamData[teamId].timelineValues[METRICS.DISTANCE] = timelineValues;
          break;
        case METRICS.WORKLOAD:
          state.teamData[teamId].timelineValues[METRICS.WORKLOAD] = timelineValues;
          break;
      }
    }
  },
  [mutation.UPDATE_TIMELINE] (state, timeline) {
    state.noData = false;
    const currentTimeline = returnTimelineObject(timeline,state.currentMetric);
    if(state.currentMetric === METRICS.TIME_ABOVE_80_PERCENT_HR) {
         state.timelineOnScreen = currentTimeline.timelineValues.map(t => {
            if(t.value !== null && !isTimelineCached(timeline)) {
              t.value = Math.floor(t.value / 60);
            }
    
            if(t.points && t.points.length && !isTimelineCached(timeline)) {
              t.points.map(p => {
                if(p.value !== null) {
                  p.value = Math.floor(p.value / 60);
                }
              })
            }
    
            return t;
          }).sort((a, b) => (new Date(a.date) - new Date(b.date)));
      state.timelinePage = 0;
      state.timelineTotalPages = Math.ceil(state.timelineOnScreen.length/state.timelineItemsPerPage);
      state.timelineMinValue = Math.ceil(currentTimeline.valueMinimum / 60) ;
      state.timelineMaxValue = Math.ceil(currentTimeline.valueMaximum / 60) ;
    } else {
      // todo
      if(isTimelineCached(timeline)){
         state.timelineOnScreen = currentTimeline.timelineValues;
      }
      else{
         state.timelineOnScreen = currentTimeline.timelineValues.sort((a, b) => (new Date(a.date) - new Date(b.date)));
      }
      state.timelinePage = 0;
      state.timelineTotalPages = Math.ceil(state.timelineOnScreen.length/state.timelineItemsPerPage);
      state.timelineMinValue = currentTimeline.valueMinimum;
      state.timelineMaxValue = currentTimeline.valueMaximum;
    }
  },
  [mutation.UPDATE_SELECTED_DASHBOARD] (state, dashboard) {
    state.noData = false;
    dashboard.timeAbove80PercentHrSeconds = Math.round(dashboard.timeAbove80PercentHrSeconds / 60);
    state.dashboardOnScreen = dashboard;
  },
  [mutation.UPDATE_INDIVIDUAL_TIMELINE] (state, { timeline, metric }) {
    state.individualTimeline[metric] = timeline;
  },
  [mutation.UPDATE_ALL_TEAMS_TIMELINE] (state, { timeline, metric }) {
    state.allTeamsTimeLine[metric] = timeline;
  },
  [mutation.GO_TO_NEXT_PAGE] (state) {
   state.timelinePage = state.timelinePage+1;
 },
 [mutation.GO_TO_PREVIOUS_PAGE] (state) {
   state.timelinePage = state.timelinePage-1;
 },
  [mutation.UPDATE_TIMERANGE_TO_DATE] (state, date) {
    state.timeRangeTo = date;
  },
  [mutation.UPDATE_TIMERANGE_FROM_DATE] (state, date) {
    state.timeRangeFrom = date;
  },
  [mutation.UPDATE_ALL_TEAMS_DASHBOARD] (state, data) {
    state.allTeamsDashboard = data;
  },
  [mutation.UPDATE_INDIVIDUAL_DASHBOARD] (state, data) {
    state.individualDashboard = data;
  },
  [mutation.RESET_TIMELINE_STATE] (state) {
    state.teamData={};
    state.allTeamsTimeLine={};
    state.individualTimeline={};
  },
  [mutation.RESET_DASHBOARD_STATE] (state) {
    state.teams = [];
    state.selectedTeamId = null;
    state.selectedTeam = null;
    state.selectedEntity = ENTITIES.TEAM;
    state.selectedTimeRange = TIME_RANGE.SESSION;
    state.timeRangeFrom = null;
    state.timeRangeTo = null;
    state.timeAbove80PercentHrSeconds = null;
    state.ap = null;
    state.distance = null;
    state.workload = null;
    state.currentMetric = METRICS.TIME_ABOVE_80_PERCENT_HR;
    state.teamData = {};
    state.individualTimeline = {
      [METRICS.TIME_ABOVE_80_PERCENT_HR]: null,
      [METRICS.AP_PERCENTAGE]: null,
      [METRICS.DISTANCE]: null,
      [METRICS.WORKLOAD]: null,
    };
    state.allTeamsTimeLine = {
      [METRICS.TIME_ABOVE_80_PERCENT_HR]: null,
      [METRICS.AP_PERCENTAGE]: null,
      [METRICS.DISTANCE]: null,
      [METRICS.WORKLOAD]: null,
    };
    state.individualDashboard = {};
    state.allTeamsDashboard = {};
    state.timelineOnScreen = null;
    state.dashboardOnScreen = {};
    state.noData = false;
    state.noSessions = false;
    state.initialDataFetched = false;
    state.timelinePage=0;
    state.timelineTotalPages = 1;
  },
  [mutation.UPDATE_DASHBOARD_NO_DATA] (state) {
    state.noData = true;
  },
  [mutation.UPDATE_NO_SESSIONS] (state, payload) {
    state.noSessions = payload;
  },
  [mutation.UPDATE_INITIAL_DATA_FETCHED] (state, dataFetched) {
    state.initialDataFetched = dataFetched;
  }
}

export default {
  state,
  actions,
  mutations,
  getters
}
